import {
  ReservationStatus,
  type LocalReservationSignatureStatus,
} from './reservationStatus';

/**
 * Trim + lowercase + collapse whitespace (and normalize dashes).
 * Applied to both `current_status` and Contentful `reservationStatus` keys
 * before they are compared.
 */
export function normalizeSalesforceStatus(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ');
}

/**
 * Profile-details `current_status` / unit `reservation_status` (and legacy
 * Salesforce strings) → internal ReservationStatus (CTAs / tone only).
 *
 * Contract:
 * - API status strings are lookup keys. Matching is case-insensitive.
 * - User-facing message: Contentful `reservationStatus.<lowercased status>`
 *   (`Booked` and key `Booked` both become `booked`). If that key is absent,
 *   the raw `current_status` string is shown as returned.
 *
 * Profile-details values (canonical — store/match exactly as returned):
 * - Booking form Sent
 * - Booking form Signed
 * - Reservation Form Pending Sign
 * - Reservation Form Signed
 * - SPA Under Customer Signature
 * - SPA Counter Sign Pending
 * - SPA Counter Signed
 * - Customer Signed SPA
 */
const SALESFORCE_STATUS_MAP: Record<string, ReservationStatus> = {
  // --- Profile-details (canonical) ---
  'booking form sent': ReservationStatus.AWAITING_CUSTOMER_SIGNATURE,
  'booking form signed': ReservationStatus.CUSTOMER_SIGNED_BOOKING_FORM,
  'reservation form pending sign':
    ReservationStatus.AWAITING_RESERVATION_FORM_CUSTOMER,
  'reservation form signed': ReservationStatus.RESERVATION_FORM_SIGNED,
  'spa under customer signature': ReservationStatus.SPA_AWAITING_CUSTOMER,
  'spa counter sign pending': ReservationStatus.SPA_AWAITING_BLOOM,
  'spa counter signed': ReservationStatus.SPA_SIGNED,
  'customer signed spa': ReservationStatus.SPA_SIGNED,

  // --- Legacy Salesforce / Excel (keep for older payloads) ---
  awaiting_customer_signature: ReservationStatus.AWAITING_CUSTOMER_SIGNATURE,
  'awaiting customer signature': ReservationStatus.AWAITING_CUSTOMER_SIGNATURE,
  'awaiting your signature': ReservationStatus.AWAITING_CUSTOMER_SIGNATURE,
  pending: ReservationStatus.AWAITING_CUSTOMER_SIGNATURE,
  customer_signed_booking_form: ReservationStatus.CUSTOMER_SIGNED_BOOKING_FORM,
  'customer signed booking form': ReservationStatus.CUSTOMER_SIGNED_BOOKING_FORM,
  opportunity_not_created: ReservationStatus.OPPORTUNITY_NOT_CREATED,
  'opportunity not yet created - generic status':
    ReservationStatus.OPPORTUNITY_NOT_CREATED,
  opportunity_created_before_sales_order:
    ReservationStatus.OPPORTUNITY_CREATED_BEFORE_SALES_ORDER,
  'intermediate status after opportunity creation and before sales order creation - generic status':
    ReservationStatus.OPPORTUNITY_CREATED_BEFORE_SALES_ORDER,
  awaiting_reservation_form_customer:
    ReservationStatus.AWAITING_RESERVATION_FORM_CUSTOMER,
  'awaiting reservation form signature by customer':
    ReservationStatus.AWAITING_RESERVATION_FORM_CUSTOMER,
  awaiting_reservation_form_bloom:
    ReservationStatus.AWAITING_RESERVATION_FORM_BLOOM,
  'awaiting reservation form signature by bloom - generic status':
    ReservationStatus.AWAITING_RESERVATION_FORM_BLOOM,
  decree_approval: ReservationStatus.DECREE_APPROVAL,
  'decree approval - generic status': ReservationStatus.DECREE_APPROVAL,
  spa_awaiting_customer: ReservationStatus.SPA_AWAITING_CUSTOMER,
  'spa - awaiting from customer': ReservationStatus.SPA_AWAITING_CUSTOMER,
  spa_awaiting_bloom: ReservationStatus.SPA_AWAITING_BLOOM,
  'spa - awaiting from bloom - generic': ReservationStatus.SPA_AWAITING_BLOOM,
};

Object.values(ReservationStatus).forEach((status) => {
  SALESFORCE_STATUS_MAP[status.toLowerCase()] = status;
});

/**
 * One input per reservation case for QA (web unit details).
 * Index 0 = not reserved. 1–8 = profile-details statuses.
 */
export const RESERVATION_STATUS_TEST_CASES = [
  '',
  'Booking form Sent',
  'Booking form Signed',
  'Reservation Form Pending Sign',
  'Reservation Form Signed',
  'SPA Under Customer Signature',
  'SPA Counter Sign Pending',
  'SPA Counter Signed',
  'Customer Signed SPA',
] as const;

/**
 * Map a raw profile-details / Salesforce status string to an internal enum.
 * Empty → not reserved (null). Unknown non-empty → UNKNOWN_RESERVED
 * (Cancel / Resend stay visible; the status line is the raw API string
 * when no Contentful key matches).
 */
export function mapSalesforceReservationStatus(
  raw: string | null | undefined,
): ReservationStatus | null {
  if (raw == null) {
    return null;
  }
  const normalized = normalizeSalesforceStatus(raw);
  if (!normalized) {
    return null;
  }
  return (
    SALESFORCE_STATUS_MAP[normalized] ?? ReservationStatus.UNKNOWN_RESERVED
  );
}

export function mapLocalReservationSignatureStatus(
  isLocallyReserved: boolean,
  localSignatureStatus?: LocalReservationSignatureStatus | null,
): ReservationStatus | null {
  if (!isLocallyReserved) {
    return null;
  }
  if (localSignatureStatus === 'confirmed') {
    return ReservationStatus.CUSTOMER_SIGNED_BOOKING_FORM;
  }
  return ReservationStatus.AWAITING_CUSTOMER_SIGNATURE;
}
